import {
    buildSyntheticOpenerMessage,
    type ChatMessage as ChatMessageType,
    getActiveMultiSelectWidget,
    getActiveSliderWidget,
    getBranchOpener,
    type MultiSelectConfirmedPayload,
    parseMultiSelectConfirmedMessage,
} from '@fruitful/llm-service-shared';
import {
    type DebtBalanceSlidersWidgetProps,
    FILING_STATUS_OPTIONS,
    HIGH_INTEREST_DEBT_OPTIONS,
    type IncomeCollectionWidgetProps,
    type MultiSelectInputWidgetProps,
    SAVINGS_GOAL_TYPE_OPTIONS,
    type SliderInputWidgetProps,
    WidgetType,
} from '@fruitful/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { resolveWelcomeOpenerIntro } from '../components/welcome-carousel/WelcomeStartMenu';

const DEBT_TYPE_RESPONSE_KEY = 'goalsDebt.debtTypes';
/** Yes/no "are you planning finances with a partner?" — asked first in the goals branches. */
const PARTNER_RESPONSE_KEY = 'goalsSetup.hasPartner';
const PARTNER_OPTIONS = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
];
const SAVINGS_GOAL_TYPE_RESPONSE_KEY = 'goalsSavings.goalType';
const SAVINGS_DEBT_CHECK_RESPONSE_KEY = 'goalsDebt.hasHighInterestDebt';
const WELCOME_RESPONSE_KEY = 'memberNotes';

/** Prompt text injected into server-emitted multi-select widgets by responseKey. */
const RESPONSE_KEY_PROMPT_TEXT: Record<string, string> = {
    [DEBT_TYPE_RESPONSE_KEY]: 'Do you have any high-interest debt?',
    [SAVINGS_GOAL_TYPE_RESPONSE_KEY]: 'What else are you saving for?',
};

const INVEST_HOUSEHOLD_TYPE_RESPONSE_KEY = 'startInvesting.householdType';
const INVEST_FILING_STATUS_RESPONSE_KEY = 'startInvesting.filingStatus';

const INVEST_HOUSEHOLD_TYPE_OPTIONS = [
    { id: 'just-me', label: 'Just me' },
    { id: 'me-and-a-partner', label: 'Me and a partner' },
];

/**
 * Invest responseKeys that the client pre-renders. Agent-called widgets
 * with these keys are suppressed while the invest state machine is active
 * so the agent can't show duplicates or fire them out of order.
 */
const INVEST_CLIENT_OWNED_KEYS = new Set([
    INVEST_HOUSEHOLD_TYPE_RESPONSE_KEY,
    INVEST_FILING_STATUS_RESPONSE_KEY,
]);

const SAVINGS_DEBT_CHECK_OPTIONS = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
];

type SavingsDebtCheckStage =
    | 'idle'
    | 'show-goal-type'
    | 'awaiting-goal-card'
    | 'show-yes-no'
    | 'done';

type MoneySystemPath = 'debt' | 'savings' | null;

/**
 * Invest path deterministic widget stages.
 *
 * idle → show-household-type → (if joint) show-filing-status → done
 *                             → (if single) done
 *
 * Household type and filing status are pre-rendered by the client.
 * Existing accounts is handled by the agent calling show-multi-select-input
 * at the right point in the conversation — the client passes it through.
 */
type InvestStage =
    | 'idle'
    | 'show-household-type'
    | 'show-filing-status'
    | 'done';

const getUserMessageText = (message: ChatMessageType): string => {
    if (message.role !== 'user') return '';
    return message.content
        .filter((c) => c.type === 'text')
        .map((c) => (c.type === 'text' ? String(c.text ?? '') : ''))
        .join('');
};

const parseUserPayload = (
    message: ChatMessageType,
): MultiSelectConfirmedPayload | null =>
    parseMultiSelectConfirmedMessage(getUserMessageText(message));

const findPayloadByResponseKey = (
    messages: ChatMessageType[],
    responseKey: string,
): MultiSelectConfirmedPayload | null => {
    for (const m of messages) {
        if (m.role !== 'user') continue;
        const payload = parseUserPayload(m);
        if (payload?.responseKey === responseKey) return payload;
    }
    return null;
};

const hasGoalCard = (messages: ChatMessageType[]): boolean =>
    messages.some(
        (m) =>
            m.role === 'assistant' &&
            m.content.some(
                (c) =>
                    c.type === 'ui' && c.widget === WidgetType.GoalSummaryCard,
            ),
    );

const DEBT_CONFIRMED_PREFIX = '[debt-details-confirmed]';

const hasDebtConfirmation = (messages: ChatMessageType[]): boolean =>
    messages.some(
        (m) =>
            m.role === 'user' &&
            getUserMessageText(m).includes(DEBT_CONFIRMED_PREFIX),
    );

/**
 * Get the active DebtBalanceSliders widget from the tail assistant message, if
 * present and the member hasn't replied yet. Returns the widget props or null.
 */
const getActiveDebtBalanceSliders = (
    messages: ChatMessageType[],
): DebtBalanceSlidersWidgetProps | null => {
    const last = messages[messages.length - 1];
    if (last?.role !== 'assistant') return null;
    for (const part of last.content) {
        if (
            part.type === 'ui' &&
            part.widget === WidgetType.DebtBalanceSliders
        ) {
            return part.props as unknown as DebtBalanceSlidersWidgetProps;
        }
    }
    return null;
};

/**
 * Get the active IncomeCollection widget from the tail assistant message, if
 * present and the member hasn't replied yet. Returns the widget props or null.
 */
const getActiveIncomeCollection = (
    messages: ChatMessageType[],
): IncomeCollectionWidgetProps | null => {
    const last = messages[messages.length - 1];
    if (last?.role !== 'assistant') return null;
    for (const part of last.content) {
        if (part.type === 'ui' && part.widget === WidgetType.IncomeCollection) {
            return part.props as unknown as IncomeCollectionWidgetProps;
        }
    }
    return null;
};

/**
 * Reconstruct the widget state machine from existing messages after a refresh.
 * Parses multi-select payloads by responseKey to determine where the flow
 * left off.
 */
interface DerivedState {
    /** Goals branches ask a partner yes/no question before the debt question. */
    partnerPending?: boolean;
    pendingDebtTypes: boolean;
    savingsStage: SavingsDebtCheckStage;
    moneySystemPath: MoneySystemPath;
    investStage: InvestStage;
    /** Synthetic assistant message to splice into the messages array. null when
     *  the branch has no pre-rendered opener or the opener phase is complete. */
    syntheticOpenerMessage: ChatMessageType | null;
    /** Whether the opener is still "active" (debt-types not yet submitted). When
     *  true the opener gates the typing indicator and streaming flags. */
    openerActive: boolean;
}

const deriveSavingsStage = (
    messages: ChatMessageType[],
): { stage: SavingsDebtCheckStage; pendingDebtTypes: boolean } => {
    const debtTypesPayload = findPayloadByResponseKey(
        messages,
        DEBT_TYPE_RESPONSE_KEY,
    );

    const goalTypePayload = findPayloadByResponseKey(
        messages,
        SAVINGS_GOAL_TYPE_RESPONSE_KEY,
    );
    if (goalTypePayload == null) {
        return { stage: 'show-goal-type', pendingDebtTypes: false };
    }

    if (!hasGoalCard(messages)) {
        return { stage: 'awaiting-goal-card', pendingDebtTypes: false };
    }

    const debtCheckPayload = findPayloadByResponseKey(
        messages,
        SAVINGS_DEBT_CHECK_RESPONSE_KEY,
    );

    if (debtCheckPayload != null) {
        const saidYes = debtCheckPayload.selectedOptionIds.includes('yes');
        return {
            pendingDebtTypes: saidYes && debtTypesPayload == null,
            stage: 'done',
        };
    }

    const firstGoalCardIndex = messages.findIndex(
        (m) =>
            m.role === 'assistant' &&
            m.content.some(
                (c) =>
                    c.type === 'ui' && c.widget === WidgetType.GoalSummaryCard,
            ),
    );
    const userRepliedAfterGoalCard = messages
        .slice(firstGoalCardIndex + 1)
        .some((m) => m.role === 'user');

    return {
        pendingDebtTypes: false,
        stage: userRepliedAfterGoalCard ? 'done' : 'show-yes-no',
    };
};

const deriveInvestStage = (messages: ChatMessageType[]): InvestStage => {
    const householdPayload = findPayloadByResponseKey(
        messages,
        INVEST_HOUSEHOLD_TYPE_RESPONSE_KEY,
    );
    if (householdPayload == null) return 'show-household-type';

    const isJoint =
        householdPayload.selectedOptionIds.includes('me-and-a-partner');
    if (isJoint) {
        const filingPayload = findPayloadByResponseKey(
            messages,
            INVEST_FILING_STATUS_RESPONSE_KEY,
        );
        if (filingPayload == null) return 'show-filing-status';
    }

    return 'done';
};

const buildOpenerMsg = (
    branchId: string | undefined,
    welcomeOptionId?: string,
): ChatMessageType | null => {
    if (branchId == null) return null;
    const opener = getBranchOpener(branchId);
    if (opener == null) return null;
    // Only the goals-system monopath varies its intro per welcome selection;
    // other branches keep their own opener text (no override).
    const introTextOverride =
        welcomeOptionId != null
            ? resolveWelcomeOpenerIntro(welcomeOptionId)
            : undefined;
    return buildSyntheticOpenerMessage(branchId, opener, introTextOverride);
};

const deriveInitialState = (messages: ChatMessageType[]): DerivedState => {
    const defaultInvest: InvestStage = 'idle';
    const noOpener = { syntheticOpenerMessage: null, openerActive: false };
    const welcomePayload = findPayloadByResponseKey(
        messages,
        WELCOME_RESPONSE_KEY,
    );
    if (welcomePayload == null) {
        return {
            pendingDebtTypes: false,
            savingsStage: 'idle',
            moneySystemPath: null,
            investStage: defaultInvest,
            ...noOpener,
        };
    }

    const selectedBranch = welcomePayload.selectedOptionIds[0];
    const welcomeOptionId = welcomePayload.welcomeOptionId;
    const debtTypesPayload = findPayloadByResponseKey(
        messages,
        DEBT_TYPE_RESPONSE_KEY,
    );
    const syntheticMsg = buildOpenerMsg(selectedBranch, welcomeOptionId);

    if (selectedBranch === 'start-investing') {
        const investSyntheticMsg = buildOpenerMsg(selectedBranch);
        const householdPayload = findPayloadByResponseKey(
            messages,
            INVEST_HOUSEHOLD_TYPE_RESPONSE_KEY,
        );
        return {
            pendingDebtTypes: false,
            savingsStage: 'idle',
            moneySystemPath: null,
            investStage: deriveInvestStage(messages),
            syntheticOpenerMessage: investSyntheticMsg,
            openerActive:
                investSyntheticMsg != null && householdPayload == null,
        };
    }

    if (syntheticMsg != null && selectedBranch !== 'create-money-system') {
        // Goals branches (get-out-of-debt / create-money-system-v2) ask the
        // partner yes/no question first (opener transition text). Until it's
        // answered we're on the partner step. After that the server drives the
        // flow — the debt question and its multi-select are emitted server-side
        // (surfaced via getActiveMultiSelectWidget), so there's no client debt
        // pre-render to arm here.
        const partnerPayload = findPayloadByResponseKey(
            messages,
            PARTNER_RESPONSE_KEY,
        );
        if (partnerPayload == null) {
            return {
                partnerPending: true,
                pendingDebtTypes: false,
                savingsStage: 'idle',
                moneySystemPath: null,
                investStage: defaultInvest,
                syntheticOpenerMessage: syntheticMsg,
                openerActive: true,
            };
        }
        return {
            partnerPending: false,
            pendingDebtTypes: false,
            savingsStage: 'idle',
            moneySystemPath: null,
            investStage: defaultInvest,
            syntheticOpenerMessage: syntheticMsg,
            openerActive: false,
        };
    }

    if (selectedBranch === 'save-for-something') {
        const savings = deriveSavingsStage(messages);
        return {
            pendingDebtTypes: savings.pendingDebtTypes,
            savingsStage: savings.stage,
            moneySystemPath: null,
            investStage: defaultInvest,
            ...noOpener,
        };
    }

    if (selectedBranch === 'create-money-system') {
        if (
            hasDebtConfirmation(messages) ||
            (debtTypesPayload != null && !hasGoalCard(messages))
        ) {
            return {
                pendingDebtTypes: false,
                savingsStage: 'idle',
                moneySystemPath: 'debt',
                investStage: defaultInvest,
                syntheticOpenerMessage: syntheticMsg,
                openerActive: false,
            };
        }

        if (hasGoalCard(messages)) {
            if (debtTypesPayload != null) {
                return {
                    pendingDebtTypes: false,
                    savingsStage: 'idle',
                    moneySystemPath: 'debt',
                    investStage: defaultInvest,
                    syntheticOpenerMessage: syntheticMsg,
                    openerActive: false,
                };
            }
            const savings = deriveSavingsStage(messages);
            return {
                pendingDebtTypes: savings.pendingDebtTypes,
                savingsStage: savings.stage,
                moneySystemPath: 'savings',
                investStage: defaultInvest,
                syntheticOpenerMessage: syntheticMsg,
                openerActive: false,
            };
        }

        return {
            pendingDebtTypes: debtTypesPayload == null,
            savingsStage: 'idle',
            moneySystemPath: null,
            investStage: defaultInvest,
            syntheticOpenerMessage: syntheticMsg,
            openerActive: debtTypesPayload == null,
        };
    }

    return {
        pendingDebtTypes: false,
        savingsStage: 'idle',
        moneySystemPath: null,
        investStage: defaultInvest,
        ...noOpener,
    };
};

/**
 * Manages client-side pre-rendered widgets that guarantee certain UI elements
 * appear regardless of whether the LLM remembers to call the right tools.
 *
 * Flows managed:
 *
 * 1. **Debt branch**: When the user selects "Get out of debt", pre-render the
 *    debt types multi-select immediately.
 *
 * 2. **Savings branch**: A forward-only state machine that pre-renders
 *    the goal type select, then the yes/no debt check after the goal card:
 *    idle → show-goal-type → awaiting-goal-card → show-yes-no → done
 *
 * 3. **Money-system path resolution**: When the user picks "Create a money
 *    system", resolves to debt or savings sub-path based on conversation.
 *
 * 4. **Invest branch**: When the user selects "Start investing", pre-renders
 *    the household type and filing status (if joint) deterministically.
 *    Existing accounts is NOT pre-rendered — the agent calls
 *    show-multi-select-input for it at the right point in the conversation.
 *    idle → show-household-type → show-filing-status (if joint) → done
 */
export const usePreRenderedWidgets = (
    messages: ChatMessageType[],
    isStreaming: boolean,
) => {
    const didInit = useRef(false);
    const isLiveSession = useRef(false);
    const isMoneySystemBranch = useRef(false);
    const [partnerPending, setPartnerPending] = useState(false);
    const partnerPendingRef = useRef(partnerPending);
    partnerPendingRef.current = partnerPending;
    const [pendingDebtTypes, setPendingDebtTypes] = useState(false);
    const [savingsStage, setSavingsStage] =
        useState<SavingsDebtCheckStage>('idle');
    const savingsStageRef = useRef<SavingsDebtCheckStage>(savingsStage);
    savingsStageRef.current = savingsStage;
    const [moneySystemPath, setMoneySystemPath] =
        useState<MoneySystemPath>(null);
    const [investStage, setInvestStage] = useState<InvestStage>('idle');
    const investStageRef = useRef<InvestStage>(investStage);
    investStageRef.current = investStage;
    const [syntheticOpenerMessage, setSyntheticOpenerMessage] =
        useState<ChatMessageType | null>(null);
    const [openerActive, setOpenerActive] = useState(false);

    useEffect(() => {
        if (didInit.current || isLiveSession.current || messages.length === 0)
            return;
        didInit.current = true;
        const restored = deriveInitialState(messages);
        setPartnerPending(restored.partnerPending ?? false);
        setPendingDebtTypes(restored.pendingDebtTypes);
        setSavingsStage(restored.savingsStage);
        setMoneySystemPath(restored.moneySystemPath);
        setInvestStage(restored.investStage);
        setSyntheticOpenerMessage(restored.syntheticOpenerMessage);
        setOpenerActive(restored.openerActive);
        if (restored.moneySystemPath != null) {
            isMoneySystemBranch.current = true;
        }
    }, [messages]);

    const activeMultiSelect = getActiveMultiSelectWidget(messages);
    // Slider is streamed by the agent/plan opener (e.g. the credit-card balance
    // question) as a data-ui widget; surface it in the footer like an
    // agent-driven multi-select. Detected purely from the messages, so it
    // survives refreshes without any local state machine.
    const activeSlider = getActiveSliderWidget(messages);
    const hasAssistantMessage =
        openerActive || messages.some((m) => m.role === 'assistant');

    const hasGoalCardInMessages = useMemo(
        () =>
            messages.some(
                (m) =>
                    m.role === 'assistant' &&
                    m.content.some(
                        (c) =>
                            c.type === 'ui' &&
                            c.widget === WidgetType.GoalSummaryCard,
                    ),
            ),
        [messages],
    );

    const resolveGoalCardStage = useCallback((): SavingsDebtCheckStage => {
        const firstGoalCardIndex = messages.findIndex(
            (m) =>
                m.role === 'assistant' &&
                m.content.some(
                    (c) =>
                        c.type === 'ui' &&
                        c.widget === WidgetType.GoalSummaryCard,
                ),
        );
        const userAlreadyReplied = messages
            .slice(firstGoalCardIndex + 1)
            .some((m) => m.role === 'user');
        return userAlreadyReplied ? 'done' : 'show-yes-no';
    }, [messages]);

    const isDebtFlow = useMemo(() => hasDebtConfirmation(messages), [messages]);

    useEffect(() => {
        if (isStreaming || !hasGoalCardInMessages) return;

        if (isDebtFlow) return;

        if (
            isMoneySystemBranch.current &&
            moneySystemPath == null &&
            findPayloadByResponseKey(messages, DEBT_TYPE_RESPONSE_KEY) == null
        ) {
            setMoneySystemPath('savings');
            setSavingsStage(resolveGoalCardStage());
            return;
        }

        if (savingsStage !== 'awaiting-goal-card') return;

        setSavingsStage(resolveGoalCardStage());
    }, [
        savingsStage,
        isStreaming,
        isDebtFlow,
        hasGoalCardInMessages,
        messages,
        moneySystemPath,
        resolveGoalCardStage,
    ]);

    const showSavingsDebtCheck =
        savingsStage === 'show-yes-no' && !isStreaming && !isDebtFlow;

    const footerWidgetProps: MultiSelectInputWidgetProps | undefined =
        useMemo(() => {
            if (
                partnerPending &&
                hasAssistantMessage &&
                (openerActive || !isStreaming)
            ) {
                return {
                    options: [...PARTNER_OPTIONS],
                    responseKey: PARTNER_RESPONSE_KEY,
                    maxSelections: 1,
                    allowCustomInput: false,
                    promptText: 'Are you planning finances with a partner?',
                };
            }
            if (pendingDebtTypes && hasAssistantMessage) {
                // goals-debt and goals-system share the same debt treatment:
                // high-interest-only options (credit cards / personal loans /
                // "I don't have high-interest debt"). Low-interest debts are
                // folded into the budget later, never selected up front.
                return {
                    options: [...HIGH_INTEREST_DEBT_OPTIONS],
                    responseKey: DEBT_TYPE_RESPONSE_KEY,
                    minSelections: 1,
                    allowCustomInput: true,
                    customInputPlaceholder: 'Something else...',
                    promptText: 'What type of high-interest debt do you have?',
                };
            }
            if (
                savingsStage === 'show-goal-type' &&
                hasAssistantMessage &&
                !isStreaming
            ) {
                return {
                    options: [...SAVINGS_GOAL_TYPE_OPTIONS],
                    responseKey: SAVINGS_GOAL_TYPE_RESPONSE_KEY,
                    maxSelections: 1,
                    allowCustomInput: true,
                    customInputPlaceholder: 'Something else...',
                    promptText: 'What else are you saving for?',
                };
            }
            if (showSavingsDebtCheck) {
                return {
                    options: [...SAVINGS_DEBT_CHECK_OPTIONS],
                    responseKey: SAVINGS_DEBT_CHECK_RESPONSE_KEY,
                    maxSelections: 1,
                    allowCustomInput: true,
                    customInputPlaceholder: 'Type your answer...',
                };
            }
            if (
                investStage === 'show-household-type' &&
                hasAssistantMessage &&
                (openerActive || !isStreaming)
            ) {
                return {
                    options: [...INVEST_HOUSEHOLD_TYPE_OPTIONS],
                    responseKey: INVEST_HOUSEHOLD_TYPE_RESPONSE_KEY,
                    maxSelections: 1,
                    allowCustomInput: true,
                    customInputPlaceholder: 'Type your answer...',
                };
            }
            if (
                investStage === 'show-filing-status' &&
                hasAssistantMessage &&
                !isStreaming
            ) {
                return {
                    options: [...FILING_STATUS_OPTIONS],
                    responseKey: INVEST_FILING_STATUS_RESPONSE_KEY,
                    maxSelections: 1,
                    allowCustomInput: true,
                    customInputPlaceholder: 'Type your answer...',
                };
            }

            const agentKey = activeMultiSelect?.props?.responseKey;
            if (
                agentKey != null &&
                INVEST_CLIENT_OWNED_KEYS.has(agentKey) &&
                investStage !== 'idle' &&
                investStage !== 'done'
            ) {
                return undefined;
            }

            if (activeMultiSelect?.props == null) return undefined;
            const { customInputPlaceholder: _, ...agentProps } =
                activeMultiSelect.props;
            return {
                ...agentProps,
                customInputPlaceholder: 'Something else...',
                promptText:
                    agentProps.promptText ??
                    RESPONSE_KEY_PROMPT_TEXT[agentProps.responseKey],
            };
        }, [
            partnerPending,
            pendingDebtTypes,
            hasAssistantMessage,
            showSavingsDebtCheck,
            savingsStage,
            investStage,
            isStreaming,
            activeMultiSelect?.props,
            openerActive,
        ]);

    useEffect(() => {
        if (moneySystemPath !== 'savings' || savingsStage !== 'idle') return;
        setSavingsStage('show-goal-type');
    }, [moneySystemPath, savingsStage]);

    const onWelcomeSubmit = useCallback((agentPayload: string) => {
        isLiveSession.current = true;
        const payload = parseMultiSelectConfirmedMessage(agentPayload);
        const branch = payload?.selectedOptionIds[0];
        if (branch === 'save-for-something') {
            setSavingsStage('show-goal-type');
        }
        if (branch === 'create-money-system') {
            isMoneySystemBranch.current = true;
        }
        const syntheticMsg = buildOpenerMsg(branch, payload?.welcomeOptionId);
        if (syntheticMsg != null) {
            // Goals branches (get-out-of-debt / create-money-system-v2) open on
            // the partner yes/no question; the debt multi-select comes after,
            // driven by the server's debt question.
            if (branch !== 'start-investing') {
                setPartnerPending(true);
            }
            setSyntheticOpenerMessage(syntheticMsg);
            setOpenerActive(true);
        }
        if (branch === 'start-investing') {
            setInvestStage('show-household-type');
        }
    }, []);

    const onFooterWidgetSubmit = useCallback(
        (agentPayload: string) => {
            const payload = parseMultiSelectConfirmedMessage(agentPayload);

            // Partner answered → hand off to the server. The next question
            // (debt for goals-debt, income for goals-system) is streamed by the
            // server, and the debt multi-select is emitted by the server's debt
            // step, so there's nothing to arm client-side here.
            if (
                partnerPendingRef.current &&
                payload?.responseKey === PARTNER_RESPONSE_KEY
            ) {
                setPartnerPending(false);
                setOpenerActive(false);
                return;
            }

            if (pendingDebtTypes) {
                setPendingDebtTypes(false);
                if (openerActive) {
                    setOpenerActive(false);
                }
            }

            if (
                isMoneySystemBranch.current &&
                moneySystemPath == null &&
                payload?.responseKey === DEBT_TYPE_RESPONSE_KEY
            ) {
                setMoneySystemPath('debt');
            }

            const stage = savingsStageRef.current;
            if (
                stage === 'show-goal-type' &&
                payload?.responseKey === SAVINGS_GOAL_TYPE_RESPONSE_KEY
            ) {
                setSavingsStage('awaiting-goal-card');
            }
            if (stage === 'show-yes-no' || stage === 'awaiting-goal-card') {
                setSavingsStage('done');
                if (payload?.selectedOptionIds.includes('yes')) {
                    setPendingDebtTypes(true);
                }
            }

            const currentInvestStage = investStageRef.current;
            if (
                currentInvestStage === 'show-household-type' &&
                payload?.responseKey === INVEST_HOUSEHOLD_TYPE_RESPONSE_KEY
            ) {
                if (openerActive) {
                    setOpenerActive(false);
                }
                const isJoint =
                    payload.selectedOptionIds.includes('me-and-a-partner');
                setInvestStage(isJoint ? 'show-filing-status' : 'done');
            }
            if (
                currentInvestStage === 'show-filing-status' &&
                payload?.responseKey === INVEST_FILING_STATUS_RESPONSE_KEY
            ) {
                setInvestStage('done');
            }
        },
        [pendingDebtTypes, moneySystemPath, openerActive],
    );

    // Detect the multi-step debt balance sliders widget from the server's
    // data-ui message. Shown once for all slider-eligible debt types.
    const footerDebtBalanceSlidersProps:
        | DebtBalanceSlidersWidgetProps
        | undefined = useMemo(() => {
        if (footerWidgetProps != null) return undefined;
        if (isStreaming) return undefined;
        const props = getActiveDebtBalanceSliders(messages);
        return props ?? undefined;
    }, [footerWidgetProps, isStreaming, messages]);

    // Detect the income collection widget from the server's data-ui message.
    const footerIncomeCollectionProps: IncomeCollectionWidgetProps | undefined =
        useMemo(() => {
            if (footerWidgetProps != null) return undefined;
            if (isStreaming) return undefined;
            const props = getActiveIncomeCollection(messages);
            return props ?? undefined;
        }, [footerWidgetProps, isStreaming, messages]);

    // Only surface a slider when there's no pending multi-select for the
    // footer, so the two footer widgets never fight over the same slot. The
    // debt-balance sliders take precedence; otherwise fall back to any
    // agent-driven slider (show-slider-input).
    const footerSliderProps: SliderInputWidgetProps | undefined =
        useMemo(() => {
            if (footerWidgetProps != null) return undefined;
            if (footerDebtBalanceSlidersProps != null) return undefined;
            if (footerIncomeCollectionProps != null) return undefined;
            return activeSlider?.props ?? undefined;
        }, [
            footerWidgetProps,
            footerDebtBalanceSlidersProps,
            footerIncomeCollectionProps,
            activeSlider?.props,
        ]);

    return {
        footerWidgetProps,
        footerSliderProps,
        footerDebtBalanceSlidersProps,
        footerIncomeCollectionProps,
        syntheticOpenerMessage,
        openerActive,
        onWelcomeSubmit,
        onFooterWidgetSubmit,
    };
};
